import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Geolocation } from '@capacitor/geolocation';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Flame, HeartPulse, ShieldAlert, UserPlus, type LucideIcon } from 'lucide-react';
import { platform } from '@shared/lib/platform';
import { toast } from '@shared/ui/feedback/Toast';
import {
  triggerPanic,
  cancelPanic,
  fetchEmergencyTypes,
  fetchMyPanicLogs,
  isActivePanicStatus,
  type PanicLog,
} from './api';

/**
 * Shared Panic/SOS interaction: emergency types, the current active alert, the
 * captured coordinates, and the hold-to-dispatch gesture + cancel. Used by both
 * the full `PanicSos` screen-equivalents and the compact home `PanicWidget` so
 * the safety-critical trigger logic lives in one place.
 */

const HOLD_MS = 1500;
const TONES = ['#b4544f', '#c98a4b', '#bd5b4a', '#7d8c5a', '#9c6b8a'];

export type Coords = { latitude: number; longitude: number; accuracy: number };

export interface EmergencyTile {
  id: string;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  color: string;
}

export const PANIC_HOLD_MS = HOLD_MS;

function iconFor(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (/medical|health|ambulance|clinic/.test(n)) return HeartPulse;
  if (/fire|smoke|evacuat/.test(n)) return Flame;
  if (/person|neighbour|guest|other/.test(n)) return UserPlus;
  return ShieldAlert;
}

function logSortTime(log: Pick<PanicLog, 'created_at'>): number {
  const ts = Date.parse(log.created_at);
  return Number.isNaN(ts) ? 0 : ts;
}

export function usePanicSos() {
  const queryClient = useQueryClient();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [locating, setLocating] = useState(true);
  const timerRef = useRef<number | null>(null);
  const holdPointerIdRef = useRef<number | null>(null);
  const holdCleanupRef = useRef<(() => void) | null>(null);

  const typesQuery = useQuery({ queryKey: ['emergency-types'], queryFn: fetchEmergencyTypes });
  const activeLogsQuery = useQuery({
    queryKey: ['my-panic-logs', 'active'],
    queryFn: () => fetchMyPanicLogs({ bucket: 'active' }),
  });

  const activeTypeIds = new Set((typesQuery.data ?? []).map((t) => t.emergency_type_id));
  const activeLogCandidates = [...(activeLogsQuery.data ?? [])]
    .filter((log) => isActivePanicStatus(log.status) && !log.closed_at)
    .sort((a, b) => logSortTime(b) - logSortTime(a));
  const activeLog = typesQuery.isLoading
    ? null
    : activeLogCandidates.find(
        (log) => !typesQuery.isSuccess || activeTypeIds.has(log.emergency_type_id),
      ) ?? null;

  // Capture the resident's location on mount (shown + dispatched with alerts).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 });
        if (!cancelled) {
          setCoords({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        }
      } catch {
        /* location optional */
      } finally {
        if (!cancelled) setLocating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tiles: EmergencyTile[] = (typesQuery.data ?? []).map((t, i) => ({
    id: t.emergency_type_id,
    title: t.name,
    subtitle: 'Hold to alert',
    icon: iconFor(t.name),
    color: t.bg_color || TONES[i % TONES.length]!,
  }));

  const clearHold = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    holdCleanupRef.current?.();
    holdCleanupRef.current = null;
    holdPointerIdRef.current = null;
    setHoldingId(null);
  };

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      holdCleanupRef.current?.();
    },
    [],
  );

  const fire = async (tile: EmergencyTile) => {
    setSending(true);
    // Always a fresh fix: the one from when the screen opened may be far from where the member is
    // now. That one is only the fallback when a fresh fix cannot be had within 3 seconds.
    let where = coords;
    try {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 3000, maximumAge: 0 });
      where = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
      setCoords(where);
    } catch {
      /* location optional */
    }
    try {
      await triggerPanic({
        emergency_type_id: tile.id,
        latitude: where?.latitude,
        longitude: where?.longitude,
        metadata: {
          source: 'resident_app',
          triggered_at: new Date().toISOString(),
          accuracy_m: where?.accuracy,
        },
      });
      if (platform.isNative()) {
        Haptics.notification({ type: NotificationType.Success }).catch(() => undefined);
      }
      toast.success('Alert raised', 'Estate security has your location.');
      await queryClient.invalidateQueries({ queryKey: ['my-panic-logs'] });
    } catch {
      toast.danger('Could not raise alert', 'Call your estate security number directly.');
    } finally {
      setSending(false);
    }
  };

  // Stationary press-and-hold; a drag (rail/page scroll) cancels it. NB: no
  // preventDefault / pointer-capture so scrolling still works.
  const startHold = (event: ReactPointerEvent<HTMLButtonElement>, tile: EmergencyTile, key: string) => {
    if (!event.isPrimary || sending || timerRef.current) return;
    clearHold();

    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    setHoldingId(key);
    holdPointerIdRef.current = pointerId;
    if (platform.isNative()) {
      Haptics.impact({ style: ImpactStyle.Medium }).catch(() => undefined);
    }

    const cancelCurrentHold = () => {
      if (holdPointerIdRef.current !== pointerId) return;
      clearHold();
    };
    const handlePointerDone = (e: PointerEvent) => {
      if (e.pointerId === pointerId) cancelCurrentHold();
    };
    const handlePointerMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      if (Math.hypot(e.clientX - startX, e.clientY - startY) > 12) cancelCurrentHold();
    };
    const handleVisibility = () => {
      if (document.hidden) cancelCurrentHold();
    };

    document.addEventListener('pointerup', handlePointerDone);
    document.addEventListener('pointercancel', handlePointerDone);
    document.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('blur', cancelCurrentHold);
    document.addEventListener('visibilitychange', handleVisibility);

    holdCleanupRef.current = () => {
      document.removeEventListener('pointerup', handlePointerDone);
      document.removeEventListener('pointercancel', handlePointerDone);
      document.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('blur', cancelCurrentHold);
      document.removeEventListener('visibilitychange', handleVisibility);
    };

    timerRef.current = window.setTimeout(() => {
      clearHold();
      void fire(tile);
    }, HOLD_MS);
  };

  const cancelMutation = useMutation({
    mutationFn: (logId: string) => cancelPanic(logId),
    onSuccess: () => {
      toast.success('Alert cancelled');
      void queryClient.invalidateQueries({ queryKey: ['my-panic-logs'] });
    },
    onError: (e) => toast.danger('Could not cancel', e instanceof Error ? e.message : undefined),
  });

  return {
    tiles,
    activeLog,
    coords,
    locating,
    sending,
    holdingId,
    startHold,
    isLoading: typesQuery.isLoading,
    isError: typesQuery.isError,
    cancelActive: (logId: string) => cancelMutation.mutate(logId),
    cancelling: cancelMutation.isPending,
  };
}
